import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useUserMap } from '../../api/org';
import { useCustomFieldLibrary, useProjectDetailsMany, useProjectMap } from '../../api/projects';
import { useActivity } from '../../api/tasks';
import type { Activity, CustomField, ProjectDetails, Task, User } from '../../api/types';
import { Avatar } from '../../components/ui/Avatar';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorState } from '../../components/ui/States';
import { formatDate, formatDateTime, formatRelative } from '../../lib/dates';
import { formatMinutes } from '../../lib/format';
import { ACTION_LABELS, APPROVAL_LABELS, FIELD_LABELS, PRIORITY_LABELS, STATUS_LABELS, SUBTYPE_LABELS } from './taskUtils';

type Ctx = {
  users: Map<string, User>;
  projects: Map<string, { name: string }>;
  details: Map<string, ProjectDetails>;
  fields: Map<string, CustomField>;
};

const EMPTY = 'не вказано';

function fmtValue(field: string, v: unknown, ctx: Ctx): string {
  if (v === null || v === undefined || v === '') return EMPTY;
  if (field === 'assigneeId' || field === 'userId') return ctx.users.get(String(v))?.name ?? 'колишній працівник';
  if (['dueOn', 'startOn', 'deadlineOn', 'nextReviewOn'].includes(field)) return formatDate(String(v));
  if (field === 'dueAt') return formatDateTime(String(v));
  if (field === 'priority') return PRIORITY_LABELS[v as keyof typeof PRIORITY_LABELS] ?? String(v);
  if (field === 'subtype') return SUBTYPE_LABELS[v as keyof typeof SUBTYPE_LABELS] ?? String(v);
  if (field === 'status') return STATUS_LABELS[String(v)] ?? String(v);
  if (field === 'approvalStatus') return APPROVAL_LABELS[v as keyof typeof APPROVAL_LABELS] ?? String(v);
  if (field === 'estimateMinutes') return formatMinutes(Number(v));
  if (field === 'projects') return ctx.projects.get(String(v))?.name ?? 'проєкт';
  if (field.startsWith('section:')) {
    const pid = field.slice(8);
    const sec = ctx.details.get(pid)?.sections.find((s) => s.id === v);
    return sec?.name ?? 'етап';
  }
  if (typeof v === 'boolean') return v ? 'так' : 'ні';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return 'змінено';
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
}

function fieldLabel(field: string, ctx: Ctx): string {
  if (field.startsWith('section:')) {
    const name = ctx.projects.get(field.slice(8))?.name;
    return name ? `Етап у «${name}»` : 'Етап';
  }
  return FIELD_LABELS[field] ?? field;
}

/** Рядки «було → стало»; для користувацьких полів — окремо кожне змінене поле. */
function changeLines(a: Activity, ctx: Ctx) {
  const lines: { label: string; from: string; to: string }[] = [];
  for (const c of a.changes) {
    if (c.field === 'customFields' && c.from && c.to && typeof c.from === 'object' && typeof c.to === 'object') {
      const from = c.from as Record<string, unknown>;
      const to = c.to as Record<string, unknown>;
      for (const k of new Set([...Object.keys(from), ...Object.keys(to)])) {
        if (JSON.stringify(from[k]) === JSON.stringify(to[k])) continue;
        const def = ctx.fields.get(k);
        const show = (x: unknown) => {
          if (x == null) return EMPTY;
          if (def?.type === 'enum') return def.options.find((o) => o.id === x)?.name ?? String(x);
          if (def?.type === 'people' && Array.isArray(x)) return x.map((id) => ctx.users.get(String(id))?.name ?? '?').join(', ');
          if (def?.type === 'date') return formatDate(String(x));
          if (typeof x === 'boolean') return x ? 'так' : 'ні';
          return String(x);
        };
        lines.push({ label: def?.name ?? 'Поле', from: show(from[k]), to: show(to[k]) });
      }
      continue;
    }
    if (c.field === 'description') {
      lines.push({ label: 'Опис', from: c.from ? 'попередній текст' : EMPTY, to: c.to ? 'новий текст' : EMPTY });
      continue;
    }
    lines.push({ label: fieldLabel(c.field, ctx), from: fmtValue(c.field, c.from, ctx), to: fmtValue(c.field, c.to, ctx) });
  }
  return lines;
}

export function TaskActivity({ task }: { task: Task }) {
  const q = useActivity(task.id);
  const users = useUserMap();
  const projects = useProjectMap();
  const fieldsQ = useCustomFieldLibrary();
  const projectIds = useMemo(() => {
    const ids = new Set(task.memberships.map((m) => m.projectId));
    for (const a of q.data ?? []) for (const c of a.changes) if (c.field.startsWith('section:')) ids.add(c.field.slice(8));
    return [...ids];
  }, [q.data, task.memberships]);
  const details = useProjectDetailsMany(projectIds);
  const ctx: Ctx = useMemo(
    () => ({ users, projects, details, fields: new Map((fieldsQ.data ?? []).map((f) => [f.id, f])) }),
    [users, projects, details, fieldsQ.data],
  );

  if (q.isLoading)
    return (
      <div className="flex flex-col gap-3 py-3" role="status" aria-label="Завантаження історії">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${60 + i * 10}%` }} />
        ))}
      </div>
    );
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />;
  const list = q.data ?? [];
  if (!list.length) return <p className="py-4 text-meta text-fg-3">Змін ще не було.</p>;

  return (
    <ol className="relative flex flex-col" aria-label="Історія змін">
      {list.map((a) => {
        const actor = a.actorId ? users.get(a.actorId) : undefined;
        const lines = changeLines(a, ctx);
        return (
          <li key={a.id} className="flex gap-3 border-b border-line py-2.5 last:border-b-0">
            <Avatar name={actor?.name ?? 'ЄРС'} size={20} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-meta">
                <span className="font-semibold text-fg">{actor?.name ?? (a.actorKind === 'system' ? 'ЄРС (автоматично)' : 'Система')}</span>
                <span className="text-fg-2"> · {ACTION_LABELS[a.action] ?? a.action}</span>
                <time dateTime={a.at} title={formatDateTime(a.at)} className="ml-1.5 text-caption text-fg-3">
                  {formatRelative(a.at)}
                </time>
              </p>
              {lines.length > 0 && (
                <dl className="mt-1 flex flex-col gap-0.5">
                  {lines.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-baseline gap-x-1.5 text-meta">
                      <dt className="text-fg-3">{l.label}:</dt>
                      <dd className="tnum flex flex-wrap items-center gap-1.5 text-fg-2">
                        <span className="line-through decoration-fg-3/60">{l.from}</span>
                        <ArrowRight className="size-3 text-fg-3" role="img" aria-label="стало" />
                        <span className="font-medium text-fg">{l.to}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {a.reason && <p className="mt-1 text-meta text-fg-2">Причина: {a.reason}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
